import { WorkflowDetails } from '../axiosApi/models/workflow-details';
import { WorkflowStateDTO } from '../axiosApi/models/workflow-state-dto';

/**
 * Workflow utility functions for working with workflow states and labels
 */
export class WorkflowHelper {
  /**
   * Get the label key for a workflow state ID from a workflow object
   * @param workflow - The workflow details object
   * @param stateId - The workflow state ID to find
   * @returns The label key string or null if not found
   */
  static getLabelKeyByStateId(workflow: WorkflowDetails | null, stateId: number | null | undefined): string | null {
    if (!workflow || !workflow.states || stateId == null) {
      return null;
    }

    const state = workflow.states.find((state: WorkflowStateDTO) => state.id === stateId);
    return state?.labelKey || null;
  }

  /**
   * Get the workflow state object by ID
   * @param workflow - The workflow details object
   * @param stateId - The workflow state ID to find
   * @returns The workflow state object or null if not found
   */
  static getStateById(workflow: WorkflowDetails | null, stateId: number | null | undefined): WorkflowStateDTO | null {
    if (!workflow || !workflow.states || stateId == null) {
      return null;
    }

    return workflow.states.find((state: WorkflowStateDTO) => state.id === stateId) || null;
  }

  /**
   * Get the display name for a workflow state (uses labelKey if available, falls back to name)
   * @param workflow - The workflow details object
   * @param stateId - The workflow state ID to find
   * @returns The display name string or null if not found
   */
  static getDisplayNameByStateId(workflow: WorkflowDetails | null, stateId: number | null | undefined): string | null {
    const state = this.getStateById(workflow, stateId);
    return state?.labelKey || state?.name || null;
  }

  /**
   * Get the color for a workflow state
   * @param workflow - The workflow details object
   * @param stateId - The workflow state ID to find
   * @returns The color string or null if not found
   */
  static getColorByStateId(workflow: WorkflowDetails | null, stateId: number | null | undefined): string | null {
    const state = this.getStateById(workflow, stateId);
    return state?.displayColor || null;
  }

  /**
   * Check if a workflow state exists
   * @param workflow - The workflow details object
   * @param stateId - The workflow state ID to check
   * @returns True if the state exists, false otherwise
   */
  static hasState(workflow: WorkflowDetails | null, stateId: number | null | undefined): boolean {
    return this.getStateById(workflow, stateId) !== null;
  }

  /**
   * Get all workflow states as a simplified array
   * @param workflow - The workflow details object
   * @returns Array of simplified state objects with id, labelKey, name, and displayColor
   */
  static getAllStates(workflow: WorkflowDetails | null): Array<{
    id: number;
    labelKey: string | null;
    name: string | null;
    displayColor: string | null;
  }> {
    if (!workflow || !workflow.states) {
      return [];
    }

    return workflow.states.map((state: WorkflowStateDTO) => ({
      id: state.id || 0,
      labelKey: state.labelKey || null,
      name: state.name || null,
      displayColor: state.displayColor || null,
    }));
  }
}

/**
 * Hook that provides workflow helper functions with the current workflow context
 * Must be used within a WorkflowProvider
 */
export const useWorkflowHelper = () => {
  // We'll implement this as a separate function to avoid circular imports
  return WorkflowHelper;
};
